import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Environment, ENVIRONMENT } from "@qid/env";
import { AlertController, ModalController } from "@ionic/angular/standalone";
import { IonSelect, IonHeader, IonToolbar, IonButton, IonTitle, IonContent, IonList, IonItem, IonSelectOption, IonToggle, IonLabel, IonIcon, IonListHeader } from "@ionic/angular/standalone";
import { ApiModules, ApiOperations, DisplayService, OrganisationUserRoles } from "@qid/core";
import { Store } from "@ngrx/store";
import { firstValueFrom } from "rxjs";
import { FormsModule } from '@angular/forms';
import { PermissionDirective } from '../../modules/organisation-user/directives/permission.directive';
import {
  MasterOrganisationUserService,
  MasterPermissionList,
  OrganisationUser,
  OrganisationUserDataRepository,
  OrganisationUserSelectors,
  PermissionList
} from "../../modules/organisation-user";
import { ZohoDepositAccount } from "../../modules/organisation-user/interface/zoho-deposit-account.interface";
import { AppName } from "../../enum/app-name.enum";
import { CommonModule } from '@angular/common';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent, AvatarRoundComponent, SuperButtonIconComponent } from '@qid/super';

@Component({
  selector: 'organisation-user-permissions',
  templateUrl: './organisation-user-permissions-page.component.html',
  styleUrls: ['./organisation-user-permissions-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    AvatarRoundComponent,
    SuperButtonIconComponent,
    SuperButtonComponent,
    PermissionDirective,
    IonSelect,
    IonHeader,
    IonToolbar,
    IonButton,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonSelectOption,
    IonToggle,
    IonLabel,
    IonIcon,
    IonListHeader
  ],
})
export class OrganisationUserPermissionsPage implements OnInit {

  @ViewChild('roleSelect') roleSelect: IonSelect

  @Input() organisationUser: OrganisationUser;
  @Input() viewOnlyMode: boolean = false;
  /** Master team only — show Zoho Payment Account ID (enables Cash on Create Order). */
  @Input() showZohoPaymentAccount = false;

  public loading = true;
  public zohoPaymentAccountId = '';
  public zohoAccount: ZohoDepositAccount | null = null;
  public savingZoho = false;
  /** Zoho can be edited by Admin+ even when viewing your own profile (viewOnlyMode). */
  public canEditZohoPaymentAccount = false;

  public permissionList = this.environment.appName == AppName.Master ? MasterPermissionList : PermissionList;

  constructor(private modalController: ModalController,
    private organisationUserRepository: OrganisationUserDataRepository,
    private masterOrganisationUserService: MasterOrganisationUserService,
    @Inject(ENVIRONMENT) private environment: Environment,
    private alertController: AlertController,
    private displayService: DisplayService,
    private store: Store
  ) {
  }

  ngOnInit() {
    this.zohoPaymentAccountId = this.organisationUser?.zohoPaymentAccountId || '';
  }

  ionViewDidEnter() {
    this.checkViewOnlyMode()
  }

  public get hasZohoPaymentAccount(): boolean {
    return !!(this.organisationUser?.zohoPaymentAccountId?.toString().trim());
  }

  public get zohoAccountLabel(): string {
    return this.zohoAccount?.accountName
      || this.organisationUser?.zohoPaymentAccountId
      || '';
  }

  async checkViewOnlyMode() {
    const activeOrgUser = await firstValueFrom(this.store.select(OrganisationUserSelectors.selectActiveOrganisationUser))

    this.canEditZohoPaymentAccount = !!activeOrgUser
      && +activeOrgUser.role <= +OrganisationUserRoles.ADMIN
      && PermissionDirective.hasPermission(activeOrgUser, ApiModules.OrganisationUser, ApiOperations.update);

    if (this.viewOnlyMode) {
      this.loading = false;
      void this.hydrateZohoAccount();
      return
    }

    if (!activeOrgUser) {
      this.loading = false;
      return
    }

    if (+this.organisationUser.role < +activeOrgUser?.role) {
      this.viewOnlyMode = true
    }

    if (this.organisationUser._id == activeOrgUser._id) {
      this.viewOnlyMode = true
    }

    if (!PermissionDirective.hasPermission(activeOrgUser, ApiModules.OrganisationUser, ApiOperations.update)) {
      this.viewOnlyMode = true
    }
    this.loading = false;
    void this.hydrateZohoAccount();
  }

  /** Re-validate existing mapping via PUT to load Zoho account name (Admin only). */
  private async hydrateZohoAccount() {
    if (!this.showZohoPaymentAccount || !this.canEditZohoPaymentAccount || !this.hasZohoPaymentAccount) {
      return;
    }
    try {
      const res = await this.masterOrganisationUserService.updateZohoPaymentAccount(
        this.organisationUser._id,
        this.organisationUser.zohoPaymentAccountId,
      );
      this.applyZohoResponse(res);
    } catch {
      // keep id-only display
    }
  }

  private applyZohoResponse(res: { organisationUser: OrganisationUser; zohoAccount: ZohoDepositAccount | null }) {
    if (res?.organisationUser) {
      this.organisationUser = { ...this.organisationUser, ...res.organisationUser };
    }
    this.zohoAccount = res?.zohoAccount || null;
    this.zohoPaymentAccountId = this.organisationUser.zohoPaymentAccountId || '';
  }

  async onPermissionToggle(event, permission) {
    let isAdd = event.detail.checked;
    const res = await this.organisationUserRepository.updatePermission(this.organisationUser._id, isAdd, permission)
    if (res?.data) {
      // Update local reference to keep permissions array in sync
      this.organisationUser = res.data
    }
  }

  getIsPermission(permission) {
    if (+this.organisationUser?.role <= +OrganisationUserRoles.ADMIN) {
      return true
    }
    if (!this.organisationUser?.permissions) {
      return false
    }
    return this.organisationUser.permissions.indexOf(permission) > -1
  }

  close() {
    this.modalController.dismiss();
  }

  deleteUser = async (orgUser_id: string) => {
    await this.organisationUserRepository.delete(orgUser_id);
    this.modalController.dismiss()
  };

  saveZohoPaymentAccount = async () => {
    if (!this.organisationUser?._id || this.savingZoho) {
      return;
    }
    this.savingZoho = true;
    try {
      const value = this.zohoPaymentAccountId?.trim() || null;
      const res = await this.masterOrganisationUserService.updateZohoPaymentAccount(
        this.organisationUser._id,
        value,
      );
      this.applyZohoResponse(res);
      await this.displayService.toast({
        message: value
          ? `Mapped to ${this.zohoAccount?.accountName || 'Zoho account'} — Cash enabled`
          : 'Zoho Payment Account cleared — Cash disabled',
        color: 'success',
      });
    } catch (e) {
      const message = typeof e === 'string' && e ? e : 'Could not save Zoho Payment Account';
      await this.displayService.toast({ message, color: 'danger' });
    } finally {
      this.savingZoho = false;
    }
  };

  clearZohoPaymentAccount = async () => {
    this.zohoPaymentAccountId = '';
    await this.saveZohoPaymentAccount();
  };

  async roleChanged(event) {
    const alert = await this.alertController.create({
      header: "Change Role?",
      buttons: [
        {
          role: "cancel",
          text: 'Cancel',
          handler: () => {
            this.roleSelect.value = this.organisationUser.role
          }
        },
        {
          text: "Confirm",
          handler: async () => {
            if (event?.detail?.value) {
              const res = await this.organisationUserRepository.updateRole(this.organisationUser._id, event?.detail?.value)
              if (res.errorMessage) {
                this.roleSelect.value = this.organisationUser.role
                this.displayService.toast({
                  message: res.errorMessage,
                  color: "danger"
                })
              } else if (res.data) {
                // Update local reference to trigger UI refresh with new permissions
                this.organisationUser = res.data
              }
            }
          }
        }
      ]
    })
    alert.present()

  }

  protected readonly ApiModules = ApiModules;
  protected readonly ApiOperations = ApiOperations;
}
